import React, { useState, useMemo } from 'react'
import styled from 'styled-components'
import RefreshIcon from '@mui/icons-material/Refresh'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Table from 'components/Table'
import BreadCrumbLink from 'components/BreadCrumbLink'
import Spinner from 'components/Spinner'
import useActiveHehmAccount from 'hooks/useActiveHehmAccount'
import { useSessionContext } from 'hooks/SessionContext'
import { useConfirmDialog, useAlertDialog } from 'hooks/useDialog'
import useDeprovisionedDevices from 'hooks/useDeprovisionedDevices'
import { shortDateTime } from 'helpers/formatters'
import Api from 'services/api'
import { color } from 'styles/colors'
import { PageHeader, PageTitle, screenSize, SinglePanePage } from 'styles/shared'
import { DeprovisionedHehmDevice } from 'types/DeprovisionedHehmDevice'

const pluralize = (count: number, word: string) => `${count} ${word}${count !== 1 ? 's' : ''}`

const DeprovisionedHehmDevices = () => {
  const { session: { dealerId } } = useSessionContext()
  const { deprovisionedDevices, isDeprovisionedDevicesLoaded, refreshDeprovisionedDevices } = useDeprovisionedDevices()
  const { activeHehmAccount, isActiveHehmAccountLoaded } = useActiveHehmAccount(dealerId)
  const [isReprovisioning, setIsReprovisioning] = useState<string | null>(null)
  const [isBulkReprovisioning, setIsBulkReprovisioning] = useState(false)
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
  const { confirm } = useConfirmDialog()
  const { alert } = useAlertDialog()

  const hasActiveAccount = Boolean(activeHehmAccount)

  const uniqueReasons = useMemo(() => {
    const reasons = deprovisionedDevices
      .map(d => d.deprovisionReason)
      .filter((reason): reason is string => reason !== null && reason !== '')
    return Array.from(new Set(reasons))
  }, [deprovisionedDevices])

  const handleReprovision = async (device: DeprovisionedHehmDevice) => {
    const isConfirmed = await confirm({
      title: 'Enable Device',
      description: `Are you sure you want to enable device ${device.deviceId}?`,
      confirmText: 'Enable'
    })
    if (!isConfirmed) return

    setIsReprovisioning(device.deviceId)
    try {
      await Api.HomeHealth.reprovisionDevice(String(dealerId), device.deviceId)
      await refreshDeprovisionedDevices()
    } catch {
      await alert({ title: 'Error', description: 'Failed to enable device. Please try again.' })
    } finally {
      setIsReprovisioning(null)
    }
  }

  const handleBulkReprovision = async (reason?: string) => {
    setMenuAnchorEl(null)

    const devicesToReprovision = reason
      ? deprovisionedDevices.filter(d => d.deprovisionReason === reason)
      : deprovisionedDevices
    const count = devicesToReprovision.length
    const description = reason
      ? `Are you sure you want to enable ${pluralize(count, 'device')} with reason "${reason}"?`
      : `Are you sure you want to enable all ${pluralize(count, 'device')}?`

    if (!await confirm({ title: 'Bulk Enable', description, confirmText: 'Enable' })) return

    setIsBulkReprovisioning(true)
    let successCount = 0; let failCount = 0
    for (const device of devicesToReprovision) {
      try {
        await Api.HomeHealth.reprovisionDevice(String(dealerId), device.deviceId)
        successCount++
      } catch { failCount++ }
    }
    setIsBulkReprovisioning(false)
    await refreshDeprovisionedDevices()

    if (failCount > 0) {
      await alert({
        title: 'Bulk Enable Complete',
        description: `Successfully enabled ${pluralize(successCount, 'device')}. Failed to enable ${pluralize(failCount, 'device')}.`
      })
    }
  }

  if (!isDeprovisionedDevicesLoaded || !isActiveHehmAccountLoaded) return <Spinner variant="page" />

  return (
    <S.Container>
      <S.PageHeader>
        <span>
          <S.PageTitle>{pluralize(deprovisionedDevices.length, 'Disabled EHMKT Device')}</S.PageTitle>
          <BreadCrumbLink toRoute={'/customers/'} label={'Customers'} direction={'back'} />
          {!hasActiveAccount && deprovisionedDevices.length > 0 && (
            <S.WarningBanner>
              To enable devices, first activate your EHMKT account in Account Settings.
            </S.WarningBanner>
          )}
        </span>
        <S.Header>
          {deprovisionedDevices.length > 0 && (
            <S.BulkAction>
              <S.BulkActionLabel>Bulk Enable</S.BulkActionLabel>
              <S.IconButton $bordered onClick={(e) => setMenuAnchorEl(e.currentTarget)} title="Bulk Enable" disabled={isBulkReprovisioning || !hasActiveAccount}>
                {isBulkReprovisioning ? <RefreshIcon fontSize="small" className="spinning" /> : <MoreVertIcon fontSize="small" />}
              </S.IconButton>
              <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={() => setMenuAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <S.MenuItem onClick={() => handleBulkReprovision()}>Enable All ({deprovisionedDevices.length})</S.MenuItem>
                {uniqueReasons.length > 0 && <S.MenuDivider />}
                {uniqueReasons.map(reason => (
                  <S.MenuItem key={reason} onClick={() => handleBulkReprovision(reason)}>
                    Enable "{reason}" ({deprovisionedDevices.filter(d => d.deprovisionReason === reason).length})
                  </S.MenuItem>
                ))}
              </Menu>
            </S.BulkAction>
          )}
        </S.Header>
      </S.PageHeader>
      <S.SinglePanePage>
        <Table items={deprovisionedDevices} noDataMessage={{ title: 'No Disabled Devices', description: 'EHMKT devices that have been disabled will show up here.' }}>
          <Table.Header>
            <Table.HeaderCell title="Device ID" />
            <Table.HeaderCell title="Name" />
            <Table.HeaderCell title="Reason" />
            <Table.HeaderCell title="Disabled On" />
            <Table.HeaderCell title="Enable" />
          </Table.Header>
          {deprovisionedDevices.map(device => (
            <Table.Row key={device.deviceId}>
              <S.Cell>{device.deviceId}</S.Cell>
              <S.Cell>{device.name || '-'}</S.Cell>
              <S.Cell>{device.deprovisionReason || '-'}</S.Cell>
              <S.Cell>{shortDateTime(device.deletedAt)}</S.Cell>
              <S.ActionCell>
                <S.IconButton onClick={() => handleReprovision(device)} title="Enable Device" disabled={isReprovisioning === device.deviceId || !hasActiveAccount}>
                  <RefreshIcon fontSize="small" className={isReprovisioning === device.deviceId ? 'spinning' : ''} />
                </S.IconButton>
              </S.ActionCell>
            </Table.Row>
          ))}
        </Table>
      </S.SinglePanePage>
    </S.Container>
  )
}

const cellStyles = `display: flex; line-height: 80px; padding-left: 10px; text-overflow: ellipsis; overflow: hidden;
  @media ${screenSize.medium} { line-height: 30px; }`

const S = {
  WarningBanner: styled.div`
    background-color: ${color('paneBackground')};
    color: ${color('danger')};
    padding: 12px 16px;
    border-radius: 4px;
    margin-bottom: 16px;
    font-size: 14px;
    `,
  Container: styled.div`
    height: 100%;
    background-color: ${color('background')};
  `,
  Cell: styled(Table.Cell)`${cellStyles}`,
  ActionCell: styled(Table.Cell)`
      ${cellStyles}; 
      align-items: center;
  `,
  PageHeader: styled(PageHeader)`
    gap: 5px;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  `,
  Header: styled.div`
      display: flex; 
      justify-content: space-between; 
      align-items: center;`,
  Top: styled.div`
      display: flex; 
      justify-content: flex-start;
  `,
  SinglePanePage: styled(SinglePanePage)`
    @media ${screenSize.small} {
      margin-top: 0;
    }
  `,
  BulkAction: styled.div`
      display: flex; 
      align-items: center; 
      gap: 8px;
  `,
  BulkActionLabel: styled.span`
      display: inline-block; 
      font-weight: bold;
  `,
  PageTitle: styled(PageTitle)`
    @media ${screenSize.small} { display: block; }
  `,
  MenuItem: styled(MenuItem)`
      font-size: 14px; 
      padding: 10px 16px;
  `,
  MenuDivider: styled.div`
      height: 1px; 
      background-color: ${color('border')}; 
      margin: 4px 0;
  `,
  IconButton: styled.button<{ $bordered?: boolean }>`
    background: none; cursor: pointer; padding: 8px; border-radius: 4px; display: flex;
    align-items: center; justify-content: center; transition: background-color 0.2s;
    border: ${({ $bordered }) => $bordered ? `1px solid ${color('border')}` : 'none'};
    &:hover { background-color: ${color('secondaryButtonHover')}; }
    &:disabled { cursor: not-allowed; opacity: 0.6; }
    .spinning { animation: spin 1s linear infinite; }
    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  `
}

export default DeprovisionedHehmDevices
